/**
 * Demo Simulation — Institution API Key & Rate Limits.
 *
 * A MOCK walkthrough of what per-institution authentication for the Trust
 * Package endpoint will look like. The endpoint itself remains unauthenticated
 * in the current MVP. This demo shows the intended production shape:
 *   1. Institution self-serves an API key.
 *   2. Every /api/trust-package call carries Authorization: Bearer <key>.
 *   3. Rate-limit + issued-package registry visible in an institution console.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  ArrowLeft, ArrowRight, KeyRound, ShieldCheck, Building2, Copy,
  RefreshCcw, CheckCircle2, Gauge, ListChecks, LockKeyhole, AlertTriangle,
} from "lucide-react";

const STEPS = [
  { n: 1, key: "register", title: "Register the institution" },
  { n: 2, key: "key",      title: "Issue an API key" },
  { n: 3, key: "call",     title: "Authenticated Trust Package call" },
  { n: 4, key: "console",  title: "Institution console" },
];

// Realistic-looking (but entirely mocked) key
function mockKey() {
  const rand = (n) => Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  return `atk_live_${rand(32)}`;
}
function mockKeyId() { return `atk_${Math.random().toString(36).slice(2, 10)}`; }


export default function WalletInstitutionApiKeyDemo() {
  const [step, setStep] = useState(1);
  const [key, setKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const issue = () => {
    setKey({ id: mockKeyId(), value: mockKey(), issued_at: new Date().toISOString(), scope: "trust_package:issue" });
    setStep(2);
  };
  const copy = async () => {
    if (!key) return;
    try { await navigator.clipboard.writeText(key.value); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* ignore */ }
  };

  return (
    <WalletShell activeTab="demos">
      <div className="max-w-5xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-6" data-testid="institution-key-demo">
        <header>
          <Link to="/wallet/demos" className="text-[11px] text-slate-500 hover:underline inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to Demo Simulation</Link>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Integrator scenario · Mock only</div>
            <h1 className="font-display text-3xl lg:text-4xl font-semibold" style={{ color: NAVY }}>Institution API Key &amp; Rate Limits</h1>
            <p className="mt-2 text-sm text-slate-600 max-w-3xl">A preview of how production Trust Package issuance will be secured. Every call is tied to a specific institution via a bearer token; rate-limits + issued-package registry are visible in a per-institution console. <strong>The live endpoint remains unauthenticated in the current MVP</strong> — this walkthrough is illustrative.</p>
          </div>
        </header>

        {/* Steps */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="key-steps">
          {STEPS.map((s, i) => {
            const active = s.n === step, done = s.n < step;
            return (
              <div key={s.n} className="flex items-center gap-1 flex-shrink-0">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold" style={{ background: done ? GOLD : active ? NAVY : "#E2E8F0", color: done ? NAVY : active ? "white" : "#94A3B8" }}>
                  {done ? "✓" : s.n}
                </div>
                <span className={`text-[11px] font-medium hidden sm:inline ${active ? "" : "text-slate-500"}`} style={active ? { color: NAVY } : undefined}>{s.title}</span>
                {i < STEPS.length - 1 && <div className="w-6 h-px bg-slate-200 mx-1" />}
              </div>
            );
          })}
        </div>

        {step === 1 && <RegisterStep onNext={issue} />}
        {step === 2 && key && <KeyStep k={key} copied={copied} onCopy={copy} onNext={() => setStep(3)} />}
        {step === 3 && key && <CallStep k={key} onNext={() => setStep(4)} />}
        {step === 4 && key && <ConsoleStep k={key} onRestart={() => { setKey(null); setStep(1); }} />}

        <MockDisclaimer />
      </div>
    </WalletShell>
  );
}


function StepCard({ eyebrow, title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 lg:p-8 space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>{eyebrow}</div>
        <h2 className="mt-1 font-display text-xl lg:text-2xl font-semibold" style={{ color: NAVY }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}


function RegisterStep({ onNext }) {
  return (
    <StepCard eyebrow="Step 1" title="ABC Bank onboards as an Altara-integrated institution">
      <div className="rounded-2xl border border-slate-200 p-5 bg-slate-50/50 space-y-2 text-[12px]" data-testid="reg-form">
        <Row label="Legal name" value="ABC Bank Limited" />
        <Row label="FSP number" value="FSP 20834" />
        <Row label="Primary contact" value="integrations@abcbank.example.co.za" />
        <Row label="Requested scopes" value="trust_package:issue · consumer_intelligence:read" />
        <Row label="Environment" value="Production" />
      </div>
      <p className="text-[13px] leading-relaxed text-slate-600">Once vetted (KYC of the institution, FSCA licence check, signed data-processing agreement), Altara issues the bearer key.</p>
      <button onClick={onNext} data-testid="key-next-1" className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold" style={{ background: GOLD, color: NAVY }}>
        <KeyRound className="w-4 h-4" /> Issue API key →
      </button>
    </StepCard>
  );
}


function KeyStep({ k, copied, onCopy, onNext }) {
  return (
    <StepCard eyebrow="Step 2" title="Altara issues a signed API key">
      <div className="rounded-2xl border-2 border-dashed p-5 space-y-3" style={{ borderColor: `${GOLD}55`, background: `${GOLD}0F` }} data-testid="issued-key">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>
          <LockKeyhole className="w-3.5 h-3.5" /> One-time reveal · store securely
        </div>
        <div className="font-mono text-[13px] break-all p-3 rounded-lg bg-slate-900 text-emerald-200" data-testid="key-value">{k.value}</div>
        <div className="flex flex-wrap gap-2 items-center text-[11px] text-slate-600">
          <button onClick={onCopy} data-testid="key-copy" className="inline-flex items-center gap-1 rounded-md px-2 py-1 border border-slate-200 hover:bg-white">
            <Copy className="w-3 h-3" /> {copied ? "Copied" : "Copy"}
          </button>
          <span className="font-mono">key_id · {k.id}</span>
          <span>scope · {k.scope}</span>
          <span>issued · {new Date(k.issued_at).toLocaleString()}</span>
        </div>
      </div>
      <div className="rounded-xl p-4" style={{ background: "#FEF3C7", border: "1px solid #FCD34D" }}>
        <div className="flex items-start gap-2 text-[12px] text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>Altara stores only a hash of the key. If it&apos;s lost, rotate it — you cannot retrieve the plaintext again.</div>
        </div>
      </div>
      <button onClick={onNext} data-testid="key-next-2" className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold" style={{ background: NAVY, color: "white" }}>
        Make the first authenticated call <ArrowRight className="w-4 h-4" />
      </button>
    </StepCard>
  );
}


function CallStep({ k, onNext }) {
  const masked = k.value.slice(0, 12) + "…" + k.value.slice(-4);
  return (
    <StepCard eyebrow="Step 3" title="Every Trust Package call carries the bearer token">
      <pre className="rounded-xl bg-slate-900 text-emerald-200 p-4 text-[11px] font-mono overflow-x-auto"><code>{`POST /api/trust-package
Authorization: Bearer ${masked}
Content-Type: application/json

{
  "institution_name": "ABC Bank",
  "advisor_name": "Nandi Molefe",
  "consumer_full_name": "Mrs Nomsa Dlamini",
  "product_type": "funeral_cover",
  "product_name": "ABC Family Funeral Plan · R30 000",
  ...
}`}</code></pre>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <TileCheck icon={ShieldCheck} label="Signature verified" tone="ok" />
        <TileCheck icon={Gauge} label="Rate limit · 47 / 100 min⁻¹" tone="ok" />
        <TileCheck icon={Building2} label="Tied to ABC Bank tenant" tone="ok" />
      </div>
      <p className="text-[13px] leading-relaxed text-slate-600">Every issued Trust Package is now attributable to a specific institution, rate-limited, and revocable in one click if the key is compromised.</p>
      <button onClick={onNext} data-testid="key-next-3" className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold" style={{ background: NAVY, color: "white" }}>
        See the institution console <ArrowRight className="w-4 h-4" />
      </button>
    </StepCard>
  );
}


function ConsoleStep({ k, onRestart }) {
  const mockPackages = [
    { id: "jrn_brh_3d0b6e21230d", consumer: "Mrs N Dlamini",   product: "Funeral R30k",        issued: "07:41", intel: "Completed · High confidence" },
    { id: "jrn_brh_a17c92e70a55", consumer: "Mr S Ndlovu",     product: "Home loan protection", issued: "08:12", intel: "In progress · developing" },
    { id: "jrn_brh_c8f204b91d3e", consumer: "Ms P Van der Merwe", product: "Term life R1.5m",   issued: "09:03", intel: "Completed · Good understanding" },
    { id: "jrn_brh_8ea1704f6c22", consumer: "Mr T Mokoena",    product: "Retirement annuity",  issued: "10:26", intel: "Not started" },
  ];
  return (
    <StepCard eyebrow="Step 4" title="Per-institution Trust Package console">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="console-metrics">
        <Metric label="Issued today" value="47" trend="+12 vs yesterday" />
        <Metric label="Completed journeys" value="34 · 72%" trend="+4 pp" good />
        <Metric label="Rate limit" value="47 / 100 · min⁻¹" trend="Healthy" good />
      </div>
      <div className="rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-4 py-2.5 text-[10px] uppercase tracking-widest text-white flex items-center gap-2" style={{ background: NAVY }}>
          <ListChecks className="w-3 h-3" /> Recent packages · signed by {k.id}
        </div>
        <div className="divide-y divide-slate-100" data-testid="console-list">
          {mockPackages.map(p => (
            <div key={p.id} className="px-4 py-3 flex items-center gap-3 text-[12px]">
              <span className="font-mono text-[10px] text-slate-500 flex-shrink-0">{p.id}</span>
              <span className="text-slate-700 truncate flex-1 min-w-0">{p.consumer} · <span className="text-slate-500">{p.product}</span></span>
              <span className="text-[10px] text-slate-500 flex-shrink-0">{p.issued}</span>
              <span className="text-[10px] font-medium flex-shrink-0" style={{ color: p.intel.startsWith("Completed") ? "#065F46" : NAVY }}>{p.intel}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={onRestart} data-testid="key-restart" className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium border border-slate-200 hover:bg-slate-50" style={{ color: NAVY }}>
          <RefreshCcw className="w-4 h-4" /> Run the demo again
        </button>
        <Link to="/wallet/demos/branch-trust-package" className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold" style={{ background: NAVY, color: "white" }}>
          Back to the branch simulation <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </StepCard>
  );
}


function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2">
      <span className="text-slate-500 text-[10px] uppercase tracking-widest">{label}</span>
      <span className="font-mono text-slate-700">{value}</span>
    </div>
  );
}
function TileCheck({ icon: Icon, label, tone }) {
  const ok = tone === "ok";
  return (
    <div className="rounded-xl p-3 border" style={{ borderColor: ok ? "#A7F3D0" : "#E2E8F0", background: ok ? "#ECFDF5" : "white" }}>
      <div className="flex items-center gap-2 text-[11px] font-medium" style={{ color: ok ? "#065F46" : NAVY }}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
    </div>
  );
}
function Metric({ label, value, trend, good }) {
  return (
    <div className="rounded-2xl border border-slate-100 p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 font-display text-xl font-semibold" style={{ color: NAVY }}>{value}</div>
      <div className="mt-0.5 text-[10px]" style={{ color: good ? "#065F46" : "#64748B" }}>{trend}</div>
    </div>
  );
}
function MockDisclaimer() {
  return (
    <div className="rounded-xl p-3 border" style={{ background: "#F1F5F9", borderColor: "#E2E8F0" }}>
      <div className="text-[11px] text-slate-600 leading-relaxed flex items-start gap-2">
        <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
        <span><strong>Mock only:</strong> the key, the metrics and the console rows on this page are illustrative. The live <code className="font-mono">POST /api/trust-package</code> endpoint currently accepts unauthenticated requests — production API-key auth lands with Phase 2 institution integrations.</span>
      </div>
    </div>
  );
}
